import { useCallback, useState } from "react";
import { useBobState } from "./hooks/useBobState";
import { NavTabs } from "./components/NavTabs";
import { VersionMarker } from "./components/VersionMarker";
import { BuildIntakePanel } from "./components/BuildIntakePanel";
import { RepoGraderPanel } from "./components/RepoGraderPanel";
import { PromptForgePanel } from "./components/PromptForgePanel";
import { ArtifactPlannerPanel } from "./components/ArtifactPlannerPanel";
import { MergePlannerPanel } from "./components/MergePlannerPanel";
import { SovereignRuntimePanel } from "./components/SovereignRuntimePanel";
import { ExportCenterPanel } from "./components/ExportCenterPanel";
import { CommandDeckPanel } from "./components/CommandDeckPanel";
import { PipelinePanel } from "./components/PipelinePanel";
import { AgentBoardPanel } from "./components/AgentBoardPanel";
import { FailureRecoveryPanel } from "./components/FailureRecoveryPanel";
import { PortfolioModePanel } from "./components/PortfolioModePanel";

export default function App() {
  const { state, setSurface, patch, togglePresentationMode, refreshSovereign, toggleAdapter } =
    useBobState();
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  const toast = useCallback((msg: string) => {
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg(null), 2400);
  }, []);

  const portfolio = state.presentationMode;

  return (
    <div className={`app-shell ${portfolio ? "portfolio-mode" : ""}`}>
      <header className="hero glass-panel gold-frame">
        <div>
          <h1>{portfolio ? "BOB — Build Ops" : "BOB — Sovereign Build Operator"}</h1>
          <p className="subtitle">
            {portfolio
              ? "Portfolio view · bob.arkmpyre.com"
              : "Build Operations Brain · local-first coding assistant · bob.arkmpyre.com"}
          </p>
          <div style={{ marginTop: "0.65rem", display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <span className="chip ok">12 product surfaces active</span>
            <span className="chip locked">No API paywall runtime</span>
            {portfolio && <span className="chip ok">Portfolio mode</span>}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", alignItems: "flex-end" }}>
          <button
            type="button"
            className={`btn ${portfolio ? "gold" : "secondary"}`}
            onClick={togglePresentationMode}
            style={{ fontSize: "0.82rem", padding: "0.4rem 0.75rem" }}
          >
            {portfolio ? "Exit portfolio mode" : "Portfolio mode"}
          </button>
          <VersionMarker />
        </div>
      </header>

      <NavTabs active={state.activeSurface} onSelect={setSurface} />

      {state.activeSurface === "deck" && (
        <CommandDeckPanel state={state} onNavigate={setSurface} />
      )}
      {state.activeSurface === "pipeline" && (
        <PipelinePanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "agents" && <AgentBoardPanel state={state} toast={toast} />}
      {state.activeSurface === "recovery" && (
        <FailureRecoveryPanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "portfolio" && (
        <PortfolioModePanel
          state={state}
          onTogglePresentation={togglePresentationMode}
          onNavigate={setSurface}
        />
      )}
      {state.activeSurface === "intake" && (
        <BuildIntakePanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "grader" && (
        <RepoGraderPanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "forge" && (
        <PromptForgePanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "planner" && (
        <ArtifactPlannerPanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "merge" && (
        <MergePlannerPanel state={state} onUpdate={patch} toast={toast} />
      )}
      {state.activeSurface === "runtime" && (
        <SovereignRuntimePanel
          state={state}
          onRefresh={refreshSovereign}
          onToggleAdapter={toggleAdapter}
        />
      )}
      {state.activeSurface === "export" && <ExportCenterPanel state={state} toast={toast} />}

      {toastMsg && <div className="toast">{toastMsg}</div>}
    </div>
  );
}
